import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type Tone = "info" | "success" | "warn" | "danger";

type NoticeOptions = {
  title: string;
  message: string;
  tone?: Tone;
  confirmLabel?: string;
};

type NoticeState = NoticeOptions & { open: boolean };

type NoticeContextValue = {
  showNotice: (opts: NoticeOptions) => void;
  showInfo:    (title: string, message: string) => void;
  showSuccess: (title: string, message: string) => void;
  showWarn:    (title: string, message: string) => void;
  showError:   (title: string, message: string) => void;
};

const NoticeContext = createContext<NoticeContextValue | null>(null);

const TONE_META: Record<Tone, { icon: string; className: string }> = {
  info:    { icon: "ℹ️", className: "notice-dialog--info" },
  success: { icon: "✅", className: "notice-dialog--success" },
  warn:    { icon: "⚠️", className: "notice-dialog--warn" },
  danger:  { icon: "❌", className: "notice-dialog--danger" },
};

export function NoticeProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<NoticeState>({
    open: false, title: "", message: "", tone: "info",
  });
  const dialogRef = useRef<HTMLDialogElement>(null);

  const showNotice = useCallback((opts: NoticeOptions) => {
    setState({ open: true, tone: "info", ...opts });
  }, []);

  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (state.open && !dlg.open) dlg.showModal();
    if (!state.open && dlg.open) dlg.close();
  }, [state.open]);

  const api = useMemo<NoticeContextValue>(() => ({
    showNotice,
    showInfo:    (title, message) => showNotice({ title, message, tone: "info"    }),
    showSuccess: (title, message) => showNotice({ title, message, tone: "success" }),
    showWarn:    (title, message) => showNotice({ title, message, tone: "warn"    }),
    showError:   (title, message) => showNotice({ title, message, tone: "danger"  }),
  }), [showNotice]);

  const tone = state.tone || "info";
  const meta = TONE_META[tone];

  return (
    <NoticeContext.Provider value={api}>
      {children}
      <dialog
        ref={dialogRef}
        onCancel={close}
        onClose={close}
        className={`notice-dialog ${meta.className}`}
      >
        <div className="notice-dialog-icon">{meta.icon}</div>
        <h3 className="notice-dialog-title">{state.title}</h3>
        {state.message.split("\n").map((line, i) => (
          <p key={i} className="notice-dialog-message">{line}</p>
        ))}
        <div className="notice-dialog-actions">
          <button type="button" className="btn btn-primary" onClick={close} autoFocus>
            {state.confirmLabel || "Aceptar"}
          </button>
        </div>
      </dialog>
    </NoticeContext.Provider>
  );
}

export function useNotice(): NoticeContextValue {
  const ctx = useContext(NoticeContext);
  if (!ctx) throw new Error("useNotice debe usarse dentro de <NoticeProvider>");
  return ctx;
}
